import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.slides.harness.engineering');
import { AfterViewInit, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

type SlideKind = 'hero' | 'problem' | 'map' | 'architecture' | 'adapter' | 'workflow' | 'governance' | 'metrics' | 'roadmap' | 'rollout' | 'close';

type Slide = {
    eyebrow: string;
    title: string;
    message: string;
    takeaway: string;
    time: string;
    kind: SlideKind;
    tone: string;
    bullets?: string[];
    tags?: string[];
    notes: string;
};

type Phase = {
    key: string;
    label: string;
    goal: string;
    owner: string;
    outputs: string[];
};

type AdapterRow = {
    name: string;
    preview: string;
    print: string;
    role: string;
    status: 'ready' | 'slot' | 'fallback';
};

type Metric = {
    label: string;
    value: string;
    caption: string;
    tone: string;
};

type RoadmapItem = {
    label: string;
    title: string;
    items: string[];
};

@Component({
    selector: 'wiz-page-slides-harness-engineering',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.slides.harness.engineering/view.scss */
:host {
  display: block;
  min-height: 100vh;
  background: #f6f7f9;
  color: #121417;
}

.harness-shell {
  --accent: #2563eb;
  --accent-soft: #eff6ff;
  min-height: 100vh;
  background: #f6f7f9;
  color: #121417;
}

.deck-progress {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 90;
  height: 4px;
  background: var(--accent);
  transition: width 220ms ease;
}

.deck-topbar {
  position: fixed;
  inset: 4px 0 auto 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #dfe3ea;
  background: rgba(255, 255, 255, 0.94);
  padding: 12px 18px;
  backdrop-filter: blur(12px);
}

.deck-brand,
.deck-status,
.deck-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-mark {
  display: inline-flex;
  height: 36px;
  width: 36px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: #121417;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 850;
  letter-spacing: 0;
}

.deck-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
}

.deck-title-small {
  margin-top: 2px;
  color: #121417;
  font-size: 14px;
  font-weight: 850;
}

.deck-status span {
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
  padding: 6px 9px;
  font-size: 12px;
  font-weight: 800;
}

.deck-actions button,
.deck-controls button,
.drawer-head button,
.outline-list button,
.phase-tabs button {
  border: 1px solid #cfd6e1;
  border-radius: 7px;
  background: #fff;
  color: #334155;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
}

.deck-actions button {
  height: 32px;
  padding: 0 10px;
}

.deck-actions button:hover,
.deck-actions button.active,
.deck-controls button:hover,
.drawer-head button:hover {
  border-color: #121417;
  background: #121417;
  color: #fff;
}

.deck-viewport {
  height: 100vh;
  overflow-y: auto;
  scroll-behavior: smooth;
  scroll-snap-type: y mandatory;
}

.harness-slide {
  min-height: 100vh;
  scroll-snap-align: start;
  padding: 92px 36px 74px;
  --accent: #2563eb;
  --accent-soft: #eff6ff;
}

.harness-slide.tone-ink {
  --accent: #121417;
  --accent-soft: #f1f5f9;
}

.harness-slide.tone-rose {
  --accent: #be123c;
  --accent-soft: #fff1f2;
}

.harness-slide.tone-emerald {
  --accent: #047857;
  --accent-soft: #ecfdf5;
}

.harness-slide.tone-sky {
  --accent: #0369a1;
  --accent-soft: #f0f9ff;
}

.harness-slide.tone-amber {
  --accent: #b45309;
  --accent-soft: #fffbeb;
}

.harness-slide.tone-indigo {
  --accent: #4338ca;
  --accent-soft: #eef2ff;
}

.harness-slide.tone-zinc {
  --accent: #475569;
  --accent-soft: #f1f5f9;
}

.slide-frame {
  display: grid;
  min-height: calc(100vh - 166px);
  max-width: 1340px;
  margin: 0 auto;
  grid-template-columns: minmax(0, 0.9fr) minmax(440px, 1.1fr);
  align-items: center;
  gap: 34px;
}

.slide-copy {
  max-width: 630px;
}

.slide-eyebrow {
  display: inline-flex;
  border: 1px solid color-mix(in srgb, var(--accent) 24%, #fff);
  border-radius: 8px;
  background: var(--accent-soft);
  color: var(--accent);
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0;
}

.slide-copy h1 {
  margin: 22px 0 0;
  color: #121417;
  font-size: 52px;
  font-weight: 900;
  line-height: 1.14;
  word-break: keep-all;
}

.slide-message {
  margin: 18px 0 0;
  color: #334155;
  font-size: 21px;
  font-weight: 650;
  line-height: 1.58;
  word-break: keep-all;
}

.slide-bullets {
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
}

.slide-bullets li {
  position: relative;
  margin-top: 11px;
  padding-left: 20px;
  color: #475569;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.58;
  word-break: keep-all;
}

.slide-bullets li::before {
  position: absolute;
  top: 10px;
  left: 0;
  height: 7px;
  width: 7px;
  border-radius: 8px;
  background: var(--accent);
  content: "";
}

.takeaway {
  margin-top: 22px;
  border-left: 4px solid var(--accent);
  border-radius: 8px;
  background: color-mix(in srgb, var(--accent-soft) 74%, #fff);
  color: #1e293b;
  padding: 15px 16px;
  font-size: 16px;
  font-weight: 850;
  line-height: 1.55;
  word-break: keep-all;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
}

.tag-row span {
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  padding: 6px 9px;
  font-size: 12px;
  font-weight: 800;
}

.slide-visual {
  min-height: 560px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.hero-visual {
  position: relative;
  height: 100%;
  min-height: 560px;
  background: #121417;
  color: #fff;
}

.hero-visual img {
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  opacity: 0.44;
}

.hero-overlay {
  position: relative;
  z-index: 1;
  padding: 34px;
}

.hero-overlay span {
  display: block;
  color: #bfdbfe;
  font-size: 13px;
  font-weight: 850;
  letter-spacing: 0;
}

.hero-overlay strong {
  display: block;
  max-width: 440px;
  margin-top: 14px;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.08;
  word-break: keep-all;
}

.hero-overlay p {
  max-width: 430px;
  margin: 16px 0 0;
  color: #e0f2fe;
  font-size: 17px;
  font-weight: 650;
  line-height: 1.5;
}

.hero-stack {
  position: absolute;
  z-index: 1;
  right: 24px;
  bottom: 24px;
  left: 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.hero-stack div {
  min-height: 126px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.66);
  padding: 14px;
  backdrop-filter: blur(8px);
}

.hero-stack small,
.hero-stack span {
  display: block;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 700;
}

.hero-stack strong {
  display: block;
  margin: 9px 0 7px;
  color: #fff;
  font-size: 22px;
  font-weight: 900;
}

.problem-visual {
  display: grid;
  height: 100%;
  min-height: 560px;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: #dfe3ea;
}

.problem-node {
  display: flex;
  min-height: 260px;
  flex-direction: column;
  justify-content: flex-end;
  background: #fff;
  padding: 26px;
}

.problem-node strong {
  color: #be123c;
  font-size: 23px;
  font-weight: 900;
}

.problem-node span {
  margin-top: 10px;
  color: #475569;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.55;
  word-break: keep-all;
}

.map-visual {
  position: relative;
  min-height: 560px;
  background: #f8fafc;
}

.map-center {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  height: 150px;
  width: 150px;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
  border: 3px solid #121417;
  border-radius: 8px;
  background: #fff;
  color: #121417;
  font-size: 25px;
  font-weight: 900;
}

.map-item {
  position: absolute;
  width: 210px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  padding: 18px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
}

.map-item strong,
.map-item span {
  display: block;
}

.map-item strong {
  color: var(--accent);
  font-size: 20px;
  font-weight: 900;
}

.map-item span {
  margin-top: 7px;
  color: #64748b;
  font-size: 14px;
  font-weight: 650;
}

.item-a {
  top: 58px;
  left: 56px;
}

.item-b {
  top: 58px;
  right: 56px;
}

.item-c {
  bottom: 58px;
  left: 56px;
}

.item-d {
  right: 56px;
  bottom: 58px;
}

.architecture-visual {
  display: flex;
  min-height: 560px;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  padding: 32px;
  background: #f8fafc;
}

.flow-row {
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  padding: 18px;
}

.flow-row.accent {
  border-color: color-mix(in srgb, var(--accent) 35%, #dfe3ea);
  background: var(--accent-soft);
}

.flow-row span,
.flow-row strong {
  display: block;
}

.flow-row span {
  color: #64748b;
  font-size: 12px;
  font-weight: 850;
}

.flow-row strong {
  margin-top: 5px;
  color: #121417;
  font-size: 22px;
  font-weight: 900;
}

.flow-arrow {
  height: 26px;
  width: 2px;
  margin-left: 28px;
  background: #94a3b8;
}

.flow-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.flow-grid div,
.trace-strip span {
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  padding: 12px 8px;
  color: #334155;
  text-align: center;
  font-size: 12px;
  font-weight: 850;
}

.trace-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 4px;
}

.adapter-visual {
  min-height: 560px;
  padding: 24px;
  background: #f8fafc;
}

.adapter-table {
  overflow: hidden;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
}

.adapter-head,
.adapter-row {
  display: grid;
  grid-template-columns: 1.2fr 0.9fr 0.9fr 0.7fr;
  gap: 12px;
  align-items: center;
  border-bottom: 1px solid #e7ebf1;
  padding: 12px 14px;
}

.adapter-head {
  background: #121417;
  color: #fff;
  font-size: 12px;
  font-weight: 850;
}

.adapter-row {
  position: relative;
  min-height: 82px;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}

.adapter-row:last-child {
  border-bottom: 0;
}

.adapter-row strong {
  color: #121417;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
}

.adapter-row p {
  grid-column: 1/-1;
  margin: -2px 0 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
}

.adapter-row em {
  justify-self: start;
  border-radius: 8px;
  padding: 5px 8px;
  font-style: normal;
  font-size: 11px;
  font-weight: 850;
}

.status-ready {
  background: #dcfce7;
  color: #166534;
}

.status-slot {
  background: #e0f2fe;
  color: #075985;
}

.status-fallback {
  background: #fef3c7;
  color: #92400e;
}

.workflow-visual {
  min-height: 560px;
  padding: 28px;
  background: #f8fafc;
}

.phase-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.phase-tabs button {
  min-height: 48px;
  background: #fff;
}

.phase-tabs button.active {
  border-color: var(--accent);
  background: var(--accent);
  color: #fff;
}

.phase-panel {
  margin-top: 18px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  padding: 28px;
}

.phase-kicker {
  color: var(--accent);
  font-size: 12px;
  font-weight: 850;
}

.phase-panel h3 {
  margin: 10px 0 0;
  color: #121417;
  font-size: 30px;
  font-weight: 900;
  line-height: 1.25;
  word-break: keep-all;
}

.phase-output-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-top: 24px;
}

.phase-output-grid span {
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #f8fafc;
  padding: 14px;
  color: #334155;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 800;
}

.governance-visual {
  display: grid;
  min-height: 560px;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 24px;
  background: #f8fafc;
}

.lane {
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  padding: 22px;
}

.lane span,
.lane strong,
.lane p {
  display: block;
}

.lane span {
  color: #64748b;
  font-size: 12px;
  font-weight: 850;
}

.lane strong {
  margin-top: 10px;
  color: #121417;
  font-size: 24px;
  font-weight: 900;
}

.lane p {
  margin: 12px 0 0;
  color: #475569;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.55;
  word-break: keep-all;
}

.product {
  border-top: 5px solid #0369a1;
}

.backend {
  border-top: 5px solid #047857;
}

.infra {
  border-top: 5px solid #b45309;
}

.qa {
  border-top: 5px solid #be123c;
}

.metrics-visual {
  display: grid;
  min-height: 560px;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  padding: 24px;
  background: #f8fafc;
}

.metric-card {
  display: flex;
  min-height: 230px;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  padding: 22px;
}

.metric-card span {
  color: #64748b;
  font-size: 13px;
  font-weight: 850;
}

.metric-card strong {
  color: #121417;
  font-size: 48px;
  font-weight: 900;
}

.metric-card p {
  margin: 0;
  color: #475569;
  font-size: 14px;
  font-weight: 650;
  line-height: 1.5;
}

.metric-emerald {
  border-top: 5px solid #047857;
}

.metric-amber {
  border-top: 5px solid #b45309;
}

.metric-rose {
  border-top: 5px solid #be123c;
}

.metric-sky {
  border-top: 5px solid #0369a1;
}

.roadmap-visual {
  position: relative;
  display: grid;
  min-height: 560px;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  align-items: center;
  padding: 30px;
  background: #f8fafc;
}

.roadmap-line {
  position: absolute;
  top: 50%;
  right: 46px;
  left: 46px;
  height: 3px;
  background: #cbd5e1;
}

.roadmap-item {
  position: relative;
  z-index: 1;
  min-height: 300px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  padding: 22px;
}

.roadmap-label {
  display: inline-flex;
  border-radius: 8px;
  background: var(--accent);
  color: #fff;
  padding: 6px 9px;
  font-size: 12px;
  font-weight: 850;
}

.roadmap-item h3 {
  margin: 18px 0 12px;
  color: #121417;
  font-size: 22px;
  font-weight: 900;
}

.roadmap-item ul {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 14px;
  font-weight: 650;
  line-height: 1.7;
}

.rollout-visual {
  display: grid;
  min-height: 560px;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 24px;
  background: #f8fafc;
}

.rollout-item,
.rollout-note {
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  padding: 22px;
}

.rollout-item h3 {
  margin: 0;
  color: #121417;
  font-size: 22px;
  font-weight: 900;
}

.rollout-item p {
  margin: 12px 0 0;
  color: #475569;
  font-size: 15px;
  font-weight: 650;
  line-height: 1.55;
}

.rollout-note {
  grid-column: 1/-1;
  background: #121417;
  color: #fff;
  font-size: 24px;
  font-weight: 900;
  line-height: 1.35;
}

.close-visual {
  display: flex;
  min-height: 560px;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  padding: 36px;
  background: #121417;
  color: #fff;
}

.close-rule {
  display: grid;
  grid-template-columns: 54px 1fr;
  align-items: center;
  gap: 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  padding: 18px;
}

.close-rule span {
  display: flex;
  height: 42px;
  width: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #fff;
  color: #121417;
  font-weight: 900;
}

.close-rule strong {
  font-size: 26px;
  font-weight: 900;
}

.close-visual p {
  margin: 18px 0 0;
  color: #dbeafe;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.5;
  word-break: keep-all;
}

.deck-controls {
  position: fixed;
  right: 20px;
  bottom: 18px;
  left: 20px;
  z-index: 75;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  max-width: 620px;
  margin: 0 auto;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  padding: 9px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.1);
  backdrop-filter: blur(12px);
}

.deck-controls button {
  height: 34px;
  padding: 0 12px;
}

.deck-controls input {
  width: 100%;
  accent-color: #121417;
}

.outline-drawer,
.notes-drawer {
  position: fixed;
  top: 4px;
  right: 0;
  bottom: 0;
  z-index: 95;
  width: min(420px, 92vw);
  transform: translateX(100%);
  border-left: 1px solid #dfe3ea;
  background: #fff;
  box-shadow: -22px 0 50px rgba(15, 23, 42, 0.14);
  transition: transform 220ms ease;
}

.outline-drawer.open,
.notes-drawer.open {
  transform: translateX(0);
}

.notes-drawer {
  padding: 20px;
}

.outline-drawer {
  overflow-y: auto;
  padding: 20px;
}

.drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #e7ebf1;
  padding-bottom: 16px;
}

.drawer-head span {
  color: #64748b;
  font-size: 12px;
  font-weight: 850;
}

.drawer-head h2 {
  margin: 3px 0 0;
  color: #121417;
  font-size: 20px;
  font-weight: 900;
}

.drawer-head button {
  height: 32px;
  padding: 0 10px;
}

.outline-list {
  display: grid;
  gap: 8px;
  margin-top: 16px;
}

.outline-list button {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 10px;
  align-items: start;
  padding: 11px;
  text-align: left;
}

.outline-list button.active {
  border-color: #121417;
  background: #f1f5f9;
}

.outline-list span {
  display: flex;
  height: 28px;
  width: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #121417;
  color: #fff;
  font-size: 12px;
  font-weight: 900;
}

.outline-list strong,
.outline-list small {
  display: block;
}

.outline-list strong {
  color: #121417;
  font-size: 13px;
  line-height: 1.35;
}

.outline-list small {
  margin-top: 4px;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}

.notes-drawer h3 {
  margin: 18px 0 0;
  color: #121417;
  font-size: 24px;
  font-weight: 900;
  line-height: 1.25;
  word-break: keep-all;
}

.notes-drawer p {
  margin: 14px 0 0;
  color: #475569;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.7;
  word-break: keep-all;
}

.notes-takeaway {
  margin-top: 18px;
  border-left: 4px solid #121417;
  border-radius: 8px;
  background: #f1f5f9;
  padding: 14px;
  color: #1e293b;
  font-size: 14px;
  font-weight: 850;
  line-height: 1.55;
}

@media (max-width: 1100px) {
  .slide-frame {
    grid-template-columns: 1fr;
    gap: 22px;
  }
  .slide-copy {
    max-width: none;
  }
  .slide-visual,
  .hero-visual,
  .problem-visual,
  .map-visual,
  .architecture-visual,
  .workflow-visual,
  .governance-visual,
  .metrics-visual,
  .roadmap-visual,
  .rollout-visual,
  .close-visual {
    min-height: 480px;
  }
}
@media (max-width: 760px) {
  .deck-topbar {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 10px 12px;
  }
  .brand-copy {
    max-width: 190px;
  }
  .deck-actions {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 2px;
  }
  .deck-status {
    margin-left: auto;
  }
  .harness-slide {
    padding: 134px 16px 84px;
  }
  .slide-frame {
    min-height: calc(100vh - 218px);
  }
  .slide-copy h1 {
    font-size: 34px;
  }
  .slide-message {
    font-size: 17px;
  }
  .slide-visual,
  .hero-visual,
  .problem-visual,
  .map-visual,
  .architecture-visual,
  .workflow-visual,
  .governance-visual,
  .metrics-visual,
  .roadmap-visual,
  .rollout-visual,
  .close-visual {
    min-height: 560px;
  }
  .hero-overlay {
    padding: 24px;
  }
  .hero-overlay strong {
    font-size: 32px;
  }
  .hero-stack,
  .problem-visual,
  .governance-visual,
  .metrics-visual,
  .rollout-visual,
  .roadmap-visual,
  .flow-grid,
  .trace-strip,
  .phase-output-grid {
    grid-template-columns: 1fr;
  }
  .hero-stack {
    position: relative;
    right: auto;
    bottom: auto;
    left: auto;
    margin: 20px 18px 0;
  }
  .problem-node {
    min-height: 150px;
  }
  .map-center {
    top: 44px;
    height: 96px;
    width: 170px;
  }
  .map-item {
    position: static;
    width: auto;
    margin: 12px 18px;
  }
  .map-visual {
    padding-top: 140px;
  }
  .adapter-head,
  .adapter-row {
    grid-template-columns: 1fr 0.8fr;
  }
  .adapter-head span:nth-child(3),
  .adapter-head span:nth-child(4) {
    display: none;
  }
  .roadmap-line {
    display: none;
  }
  .deck-controls {
    right: 10px;
    left: 10px;
  }
}`],
})
export class PageSlidesHarnessEngineeringComponent implements OnInit, AfterViewInit, OnDestroy {
    public slides: Slide[] = [
        {
            eyebrow: 'Opening',
            title: 'Harness Engineering 적용 전략',
            message: '문서와 발표자료 생성, 미리보기, PDF 출력, 추적을 직접 연결하지 않고 교체 가능한 실행 하네스로 감싸는 방식입니다.',
            takeaway: '핵심 메시지: 기능을 빨리 붙이는 것보다 실행 경계를 분리해야 오래 확장됩니다.',
            time: '0:50',
            kind: 'hero',
            tone: 'ink',
            tags: ['Adapter', 'Capability Matrix', 'Trace', 'Fallback', 'Governance'],
            notes: '첫 장에서는 하네스를 테스트 도구로만 보지 말고 운영 가능한 실행 경계로 정의한다. 현재 프로젝트의 Document Harness가 이 발표의 사례라고 연결한다.'
        },
        {
            eyebrow: 'Why',
            title: '문제는 렌더러가 아니라 결합도입니다',
            message: 'HTML, PDF, 브라우저 인쇄, 서버 렌더링을 한 흐름에 직접 묶으면 작은 변경이 미리보기, 출력, 감사 기록을 동시에 흔듭니다.',
            takeaway: '하네스는 기능 추가의 속도와 운영 안정성을 동시에 지키기 위한 완충 계층입니다.',
            time: '1:10',
            kind: 'problem',
            tone: 'rose',
            bullets: ['렌더링 방식이 늘어날수록 조건문과 예외 처리가 증가', '장애 시 fallback 기준과 책임자가 불명확', '어떤 입력이 어떤 출력으로 바뀌었는지 추적하기 어려움', '향후 Playwright, PDF.js, WeasyPrint 같은 확장을 붙이기 어려움'],
            notes: '현재 불편을 네 가지 증상으로 정리한다. 특정 기술의 문제가 아니라 연결 방식의 문제라는 점을 강조한다.'
        },
        {
            eyebrow: 'Definition',
            title: '하네스는 실행 가능한 계약입니다',
            message: '각 adapter는 동일한 입력 계약을 받고, capability matrix로 가능한 동작을 공개하며, job과 trace로 실행 결과를 남깁니다.',
            takeaway: '하네스 엔지니어링은 “무엇을 실행할 수 있는가”와 “어떻게 증명할 것인가”를 같은 설계 안에 둡니다.',
            time: '1:20',
            kind: 'map',
            tone: 'emerald',
            bullets: ['Adapter: 실행 전략을 작은 모듈로 격리', 'Matrix: 현재 가능한 기능과 준비 상태 공개', 'Trace: 입력, 출력, duration, adapter version 기록', 'Fallback: 실패 시 사용자 경험을 끊지 않는 대체 경로'],
            notes: '용어 정의 슬라이드다. 추상적인 방법론보다 코드에 있는 네 요소를 중심으로 설명한다.'
        },
        {
            eyebrow: 'Current State',
            title: '현재 프로젝트에는 Document Harness MVP가 있습니다',
            message: 'archive 패키지는 HTML/PDF 미리보기와 인쇄 흐름을 adapter registry, capability matrix, job/trace 기록으로 분리하고 있습니다.',
            takeaway: '이미 시작된 구조를 표준 패턴으로 승격하면 다른 문서 생성 기능에도 반복 적용할 수 있습니다.',
            time: '1:30',
            kind: 'architecture',
            tone: 'sky',
            bullets: ['src/portal/archive/model/struct/harness.py: adapter 등록과 실행', 'src/portal/archive/app/viewer: matrix와 trace를 화면에 노출', 'src/portal/archive/model/struct/document.py: viewer와 print payload 호출 지점'],
            notes: '코드 위치를 근거로 제시한다. “없는 것을 새로 만들자”가 아니라 “이미 있는 MVP를 패턴화하자”는 제안으로 말한다.'
        },
        {
            eyebrow: 'Adapter Strategy',
            title: '렌더링 기능은 adapter matrix로 관리합니다',
            message: '지원 여부를 코드 내부 조건으로 숨기지 않고, preview, print, search, range request 같은 능력을 matrix로 드러냅니다.',
            takeaway: '기능 확장은 adapter 추가이고, 운영 판단은 matrix 확인입니다.',
            time: '1:20',
            kind: 'adapter',
            tone: 'amber',
            bullets: ['HTML iframe은 즉시 미리보기와 브라우저 인쇄에 적합', 'Native PDF는 PDF 문서의 기본 viewer와 출력에 적합', 'PDF.js, Playwright, WeasyPrint는 확장 슬롯으로 단계 도입 가능'],
            notes: 'adapter matrix를 제품 운영 언어로 번역한다. 준비된 기능과 아직 슬롯인 기능을 한눈에 구분하게 만드는 것이 중요하다.'
        },
        {
            eyebrow: 'Workflow',
            title: '적용 흐름은 네 단계로 나눕니다',
            message: '먼저 관측 가능한 실행 경계를 만들고, 그 다음 adapter를 늘리며, 마지막에 자동화와 정책으로 확장합니다.',
            takeaway: '처음부터 완전 자동화가 아니라 관측, 격리, 확장, 운영의 순서가 안전합니다.',
            time: '1:40',
            kind: 'workflow',
            tone: 'indigo',
            bullets: ['Observe: 현재 실행과 실패 지점을 기록', 'Isolate: preview/print/search를 adapter 계약으로 격리', 'Expand: 서버 렌더러와 고급 viewer를 교체 가능하게 추가', 'Operate: SLA, 권한, 회귀 테스트, 릴리즈 기준으로 관리'],
            notes: '적용 로드맵의 핵심 장이다. 청중이 바로 실행 계획으로 받아들일 수 있게 단계별 산출물을 함께 말한다.'
        },
        {
            eyebrow: 'Governance',
            title: '책임은 화면, 서버, adapter, 운영으로 나눕니다',
            message: '하네스가 안정적으로 동작하려면 누가 입력을 검증하고, 누가 렌더러를 승인하고, 누가 장애를 판단하는지 명확해야 합니다.',
            takeaway: '기술 하네스만으로는 부족하고, 책임 하네스까지 같이 설계해야 합니다.',
            time: '1:20',
            kind: 'governance',
            tone: 'zinc',
            bullets: ['Product: 사용자가 이해할 수 있는 상태와 fallback 문구', 'Backend: adapter 계약, job 상태, trace 저장', 'Infra: Playwright/WeasyPrint 같은 실행 환경', 'QA: 샘플 문서, 회귀 테스트, 출력 품질 기준'],
            notes: '기술 발표가 조직 적용 발표로 넘어가는 지점이다. RACI까지 과하게 들어가지 말고 책임 구역을 나눠 보여준다.'
        },
        {
            eyebrow: 'Metrics',
            title: '성공 기준은 “동작한다”보다 구체적이어야 합니다',
            message: '하네스 적용의 성과는 렌더링 성공률, fallback 빈도, 평균 준비 시간, trace 누락률로 판단합니다.',
            takeaway: '측정 기준이 있어야 adapter 교체가 감이 아니라 운영 의사결정이 됩니다.',
            time: '1:15',
            kind: 'metrics',
            tone: 'emerald',
            bullets: ['Preview ready rate', 'Print completion rate', 'Fallback rate by adapter', 'Trace completeness', 'Average render preparation time'],
            notes: '지표는 간단하게 둔다. MVP에서는 모든 값을 정확히 자동화하지 않아도, 이름을 정하고 기록 구조를 먼저 만들면 된다.'
        },
        {
            eyebrow: 'Roadmap',
            title: '90일 적용 계획으로 작게 시작합니다',
            message: '1개월 차에는 관측과 계약을 고정하고, 2개월 차에는 확장 adapter를 붙이며, 3개월 차에는 운영 기준과 회귀 테스트를 정리합니다.',
            takeaway: '하네스 엔지니어링은 큰 개편이 아니라 작은 실행 경계를 반복해 만드는 일입니다.',
            time: '1:30',
            kind: 'roadmap',
            tone: 'sky',
            bullets: ['0-30일: matrix와 trace를 기준으로 현재 흐름 안정화', '31-60일: PDF.js/Playwright 확장 슬롯 검증', '61-90일: 운영 지표, 장애 대응, 샘플 문서 회귀 테스트'],
            notes: '현실적인 적용 계획을 제안한다. 당장 할 수 있는 것과 나중에 붙일 것을 분리해 과한 약속처럼 보이지 않게 한다.'
        },
        {
            eyebrow: 'Expansion',
            title: '한 번 만든 하네스는 다른 생성 흐름에도 재사용됩니다',
            message: '문서 아카이브에서 시작한 패턴은 발표자료 생성, 보고서 export, 이미지/표 렌더링, 외부 저장소 연동으로 넓힐 수 있습니다.',
            takeaway: '하네스의 가치는 단일 기능보다 반복 가능한 적용 패턴에 있습니다.',
            time: '1:20',
            kind: 'rollout',
            tone: 'amber',
            bullets: ['발표자료 HTML/PPTX export', '보고서 PDF 출력과 인쇄 프로파일', '증거 이미지와 출처 asset 검증', '외부 저장소, 권한, 감사 로그 연계'],
            notes: '확장 가능성을 보여준다. 다만 모든 것을 한 번에 만들겠다는 식이 아니라 우선순위가 있는 후보군으로 제시한다.'
        },
        {
            eyebrow: 'Close',
            title: '적용 원칙은 세 문장으로 정리됩니다',
            message: '직접 연결하지 말고 adapter로 감싸고, 가능한 기능을 matrix로 공개하고, 모든 실행은 trace로 남깁니다.',
            takeaway: 'Harness Engineering은 기능을 빠르게 바꾸면서도 운영 책임을 잃지 않기 위한 설계 습관입니다.',
            time: '0:50',
            kind: 'close',
            tone: 'ink',
            bullets: ['Adapter로 격리한다', 'Matrix로 드러낸다', 'Trace로 증명한다'],
            notes: '마지막은 세 문장만 남긴다. 질문이 나오면 현재 archive 패키지를 기준으로 어디까지 구현되어 있고 다음 단계가 무엇인지 답한다.'
        }
    ];

    public phases: Phase[] = [
        {
            key: 'observe',
            label: 'Observe',
            goal: '현재 preview/print 실행 경로를 기록하고 실패 지점을 찾습니다.',
            owner: 'Backend + QA',
            outputs: ['job_id', 'duration_ms', 'adapter key', 'input/output hash']
        },
        {
            key: 'isolate',
            label: 'Isolate',
            goal: '기능별 실행 전략을 adapter 계약 뒤로 분리합니다.',
            owner: 'Backend',
            outputs: ['BaseAdapter', 'capabilities()', 'preview()', 'print_payload()']
        },
        {
            key: 'expand',
            label: 'Expand',
            goal: 'PDF.js, Playwright, WeasyPrint 같은 확장 슬롯을 검증합니다.',
            owner: 'Infra + Product',
            outputs: ['availability check', 'fallback policy', 'sample outputs']
        },
        {
            key: 'operate',
            label: 'Operate',
            goal: '운영 지표와 회귀 테스트로 품질 기준을 고정합니다.',
            owner: 'QA + Ops',
            outputs: ['SLA', 'fallback rate', 'trace completeness', 'release checklist']
        }
    ];

    public adapterRows: AdapterRow[] = [
        { name: 'html_iframe', preview: 'HTML', print: 'Browser', role: 'HTML 원본 미리보기와 즉시 인쇄 fallback', status: 'ready' },
        { name: 'native_pdf', preview: 'PDF', print: 'Open PDF', role: 'PDF 문서의 기본 뷰어와 출력', status: 'ready' },
        { name: 'pdfjs', preview: 'PDF.js', print: 'Open PDF', role: '검색과 range request를 위한 고급 뷰어 슬롯', status: 'slot' },
        { name: 'playwright_print', preview: '-', print: 'Server PDF', role: '서버 렌더링과 썸네일 생성 확장 슬롯', status: 'fallback' },
        { name: 'weasyprint', preview: '-', print: 'Server PDF', role: 'HTML 기반 서버 PDF 생성 확장 슬롯', status: 'fallback' }
    ];

    public metrics: Metric[] = [
        { label: 'Preview ready', value: '99%+', caption: '문서 선택 후 미리보기 준비 성공률', tone: 'emerald' },
        { label: 'Fallback rate', value: '< 5%', caption: '기본 adapter 실패 후 대체 경로 사용 비율', tone: 'amber' },
        { label: 'Trace gap', value: '0%', caption: 'job 실행 후 trace가 남지 않은 비율', tone: 'rose' },
        { label: 'Prep time', value: '< 1s', caption: 'payload 준비 평균 시간', tone: 'sky' }
    ];

    public roadmap: RoadmapItem[] = [
        { label: '0-30일', title: 'MVP 고정', items: ['adapter matrix 정리', 'trace 필드 표준화', '샘플 문서 회귀 세트'] },
        { label: '31-60일', title: '확장 검증', items: ['PDF.js viewer PoC', 'Playwright availability check', 'fallback 메시지 표준화'] },
        { label: '61-90일', title: '운영 전환', items: ['지표 대시보드', '릴리즈 체크리스트', 'adapter versioning'] }
    ];

    public rolloutItems = [
        { title: '문서 아카이브', caption: 'HTML/PDF preview와 print payload' },
        { title: '발표자료 export', caption: 'HTML 덱, PDF, PPTX 생성 경로 분리' },
        { title: '보고서 출력', caption: '인쇄 프로파일과 출처 asset 검증' },
        { title: '외부 연동', caption: '권한, 저장소, 감사 로그 adapter화' }
    ];

    public currentIndex = 0;
    public notesOpen = false;
    public outlineOpen = false;
    public activePhase = 'observe';
    private observer?: IntersectionObserver;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
    }

    public ngAfterViewInit() {
        setTimeout(() => this.observeSlides(), 0);
    }

    public ngOnDestroy() {
        if (this.observer) this.observer.disconnect();
    }

    public get currentSlide() {
        return this.slides[this.currentIndex] || this.slides[0];
    }

    public get progressWidth() {
        return `${((this.currentIndex + 1) / this.slides.length) * 100}%`;
    }

    public go(index: number) {
        const next = Math.max(0, Math.min(this.slides.length - 1, Number(index)));
        this.currentIndex = next;
        const element = document.getElementById(`harness-slide-${next}`);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    public next() {
        this.go(this.currentIndex + 1);
    }

    public prev() {
        this.go(this.currentIndex - 1);
    }

    public setPhase(key: string) {
        this.activePhase = key;
    }

    public selectedPhase() {
        return this.phases.find((phase) => phase.key === this.activePhase) || this.phases[0];
    }

    public statusClass(status: AdapterRow['status']) {
        if (status === 'ready') return 'status-ready';
        if (status === 'slot') return 'status-slot';
        return 'status-fallback';
    }

    public metricClass(tone: string) {
        return `metric-${tone}`;
    }

    @HostListener('document:keydown', ['$event'])
    public onKeydown(event: KeyboardEvent) {
        const target = event.target as HTMLElement;
        if (target && ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName)) return;

        if (event.key === 'ArrowRight' || event.key === 'PageDown' || event.key === ' ') {
            event.preventDefault();
            this.next();
        }
        if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
            event.preventDefault();
            this.prev();
        }
        if (event.key === 'Home') this.go(0);
        if (event.key === 'End') this.go(this.slides.length - 1);
        if (event.key.toLowerCase() === 'n') this.notesOpen = !this.notesOpen;
        if (event.key.toLowerCase() === 'o') this.outlineOpen = !this.outlineOpen;
        if (event.key === 'Escape') {
            this.notesOpen = false;
            this.outlineOpen = false;
        }
    }

    private observeSlides() {
        const sections = Array.from(document.querySelectorAll<HTMLElement>('.harness-slide'));
        if (!sections.length) return;

        this.observer = new IntersectionObserver((entries) => {
            const visible = entries
                .filter((entry) => entry.isIntersecting)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (!visible) return;
            const index = Number((visible.target as HTMLElement).dataset.index || 0);
            if (!Number.isNaN(index)) this.currentIndex = index;
        }, { threshold: [0.45, 0.65, 0.85] });

        sections.forEach((section) => this.observer?.observe(section));
    }
}

export default PageSlidesHarnessEngineeringComponent;